// hooks/useConsultation.js
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  createNewKonsultasjon as createNewKonsultasjonService,
  updateKonsultasjon as updateKonsultasjonService,
  deleteKonsultasjon as deleteKonsultasjonService,
  deleteAllKonsultasjoner as deleteAllKonsultasjonerService,
  fetchKonsultasjoner,
  generateNote as generateNoteService
} from '../services/konsultasjonService';

/**
 * Custom hook for managing consultation state and operations
 * @param {string} apiBaseUrl - The base URL for the API
 * @param {string} userId - The current user ID
 * @param {Object} socket - The socket.io instance
 */
const useConsultation = (apiBaseUrl, userId, socket, config = {}) => {
  // State for consultations and the current consultation
  const [konsultasjoner, setKonsultasjoner] = useState([]);
  const [currentKonsultasjon, setCurrentKonsultasjon] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [note, setNote] = useState('');
  const [contextText, setContextText] = useState('');
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [isTranscriptionLocked, setIsTranscriptionLocked] = useState(true);
  
  // Refs for tracking state between renders
  const currentKonsultasjonRef = useRef(null);
  const contextTextRef = useRef(contextText);
  const userIdRef = useRef(userId);

  // Update refs when state changes
  useEffect(() => {
    currentKonsultasjonRef.current = currentKonsultasjon;
  }, [currentKonsultasjon]);

  useEffect(() => {
    contextTextRef.current = contextText;
  }, [contextText]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Fetch consultations when userId changes
  useEffect(() => {
    if (!userId) {
      console.log("No userId available, skipping consultation fetch");
      return;
    }
    
    const fetchAllKonsultasjoner = async () => {
      try {
        console.log(`Fetching consultations for user ID: ${userId}`);
        const fetched = await fetchKonsultasjoner(apiBaseUrl, userId);
        console.log(`Received ${fetched.length} consultations for user ${userId}`);
        
        // Log the first consultation for debugging
        if (fetched.length > 0) {
          console.log(`First consultation: ID=${fetched[0].id}, user_id=${fetched[0].user_id}`);
        }
        
        const sorted = [...fetched].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
        setKonsultasjoner(sorted);
      } catch (err) {
        console.error(`Error fetching consultations for user ${userId}:`, err);
      }
    };
    
    fetchAllKonsultasjoner();
  }, [apiBaseUrl, userId]);

  // Update context and note when the current consultation changes
  useEffect(() => {
    if (currentKonsultasjon) {
      setContextText(currentKonsultasjon.kontekst || "");
      setNote(currentKonsultasjon.notat || "");
      
      // Ensure the current consultation has the correct user_id
      if (currentKonsultasjon.user_id !== userId && userId) {
        console.log(`Fixing user_id mismatch: consultation user_id=${currentKonsultasjon.user_id}, current userId=${userId}`);
        const fixedKonsultasjon = {
          ...currentKonsultasjon,
          user_id: userId
        };
        setCurrentKonsultasjon(fixedKonsultasjon);
      }
    } else {
      setContextText("");
      setNote("");
    }
  }, [currentKonsultasjon?.id, userId]);

  // Poll for transcription updates
  useEffect(() => {
    if (!currentKonsultasjon || !userId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/konsultasjoner/${currentKonsultasjon.id}/transcript`, {
          headers: {
            'X-User-Id': userId
          }
        });
        if (isTranscriptionLocked && res.data.transcript !== transcription) {
          setTranscription(res.data.transcript);
        }
      } catch (error) {
        console.error('Error fetching transcript:', error);
      }
    }, 1000);
    
    return () => clearInterval(pollInterval);
  }, [currentKonsultasjon, apiBaseUrl, transcription, isTranscriptionLocked, userId]);

  // Handle socket room management
  useEffect(() => {
    if (!socket || !currentKonsultasjon) return;
    
    socket.emit('join_room', { konsultasjon_id: currentKonsultasjon.id });
    
    return () => {
      socket.emit('leave_room', { konsultasjon_id: currentKonsultasjon.id });
    };
  }, [socket, currentKonsultasjon]);
  
  // Handle socket reconnection
  useEffect(() => {
    if (!socket) return;
    
    const handleReconnect = () => {
      if (currentKonsultasjon) {
        socket.emit('join_room', { konsultasjon_id: currentKonsultasjon.id });
      }
    };
    
    socket.on('connect', handleReconnect);
    
    return () => socket.off('connect', handleReconnect);
  }, [socket, currentKonsultasjon]);

  // Handle transcription updates from socket
  useEffect(() => {
    if (!socket) return;
  
    const handleTranscriptionUpdate = (data) => {
      if (!data.transcription) return;
      if (!isTranscriptionLocked) return;
      
      setTranscription(prev => {
        // Always use the current user ID - critically important!
        const currentUserId = userIdRef.current;
        
        if (!currentUserId) {
          console.error("Missing userId in transcription update handler");
          return prev;
        }
        
        console.log(`Transcription update for user ${currentUserId}, consultation ${currentKonsultasjonRef.current?.id}`);
        
        // If this is an update (new bit of transcription)
        if (data.is_update) {
          const newTranscript = prev + (prev ? "\n" : "") + data.transcription;
          
          if (currentKonsultasjonRef.current) {
            const updated = { 
              ...currentKonsultasjonRef.current, 
              transcript: newTranscript,
              user_id: currentUserId // Always use the current user ID
            };
            setCurrentKonsultasjon(updated);
            currentKonsultasjonRef.current = updated;
          }
          
          return newTranscript;
        } else {
          // This is a full replacement (e.g. on first load)
          if (currentKonsultasjonRef.current) {
            const updated = { 
              ...currentKonsultasjonRef.current, 
              transcript: data.transcription,
              user_id: currentUserId // Always use the current user ID
            };
            setCurrentKonsultasjon(updated);
            currentKonsultasjonRef.current = updated;
          }
          return data.transcription;
        }
      });
    };
    
    const handleConnect = () => {
      socket.off('transcription_update', handleTranscriptionUpdate);
      socket.on('transcription_update', handleTranscriptionUpdate);
    };
  
    socket.on('transcription_update', handleTranscriptionUpdate);
    socket.on('connect', handleConnect);
    
    return () => {
      socket.off('transcription_update', handleTranscriptionUpdate);
      socket.off('connect', handleConnect);
    };
  }, [socket, isTranscriptionLocked]);

  /**
   * Create a new consultation
   * @param {string} chosenTemplate - The template to use
   * @param {string} defaultGptModel - The default GPT model
   * @param {Object} config - Configuration settings
   */
  const createNewKonsultasjon = async (context = "", chosenTemplate, defaultGptModel) => {
    try {
      // Make sure to always include userId
      if (!userId) {
        console.error("Cannot create consultation: No user ID available");
        throw new Error("No user ID available");
      }
      
      console.log(`Creating new consultation for user ${userId}`);
      
      const created = await createNewKonsultasjonService(
        apiBaseUrl, 
        userId, 
        context, 
        chosenTemplate, 
        config?.gpt_model_choice || defaultGptModel
      );
      
      // Double-check that returned consultation has correct user_id
      if (created.user_id !== userId) {
        console.warn(`API returned consultation with incorrect user_id: ${created.user_id}, correcting to ${userId}`);
      }
      
      // Ensure created consultation has correct user_id
      const withUserId = {
        ...created,
        user_id: userId
      };
      
      console.log(`New consultation created: ID=${withUserId.id}, user_id=${withUserId.user_id}`);
      
      setKonsultasjoner(prev => [withUserId, ...prev]);
      setTranscription('');
      setNote('');
      
      return withUserId;
    } catch (error) {
      console.error(`Error creating consultation for user ${userId}:`, error);
      throw error;
    }
  };

  /**
   * Update an existing consultation
   * @param {Object} updated - The updated consultation object
   */
  const updateKonsultasjon = async (updated) => {
    try {
      // Ensure update has the current userId
      const updateWithUserId = {
        ...updated,
        user_id: userId
      };
      
      await updateKonsultasjonService(apiBaseUrl, userId, updateWithUserId);
      
      setCurrentKonsultasjon(updateWithUserId);
      setKonsultasjoner(prev =>
        prev.map(k => k.id === updateWithUserId.id ? updateWithUserId : k)
      );
    } catch (error) {
      console.error(`Error updating consultation for user ${userId}:`, error);
    }
  };

  /**
   * Delete a consultation
   * @param {Object} konsultasjon - The consultation to delete
   */
  const handleDeleteKonsultasjon = async (konsultasjon) => {
    if (!window.confirm(`Er du sikker på at du vil slette konsultasjonen "${konsultasjon.title || 'Uten tittel'}"?`)) {
      return;
    }
    
    try {
      await deleteKonsultasjonService(apiBaseUrl, userId, konsultasjon.id);
      
      setKonsultasjoner(prev => prev.filter(k => k.id !== konsultasjon.id));
      
      if (currentKonsultasjon?.id === konsultasjon.id) {
        setCurrentKonsultasjon(null);
        setTranscription('');
        setNote('');
      }
    } catch (error) {
      console.error(`Error deleting consultation for user ${userId}:`, error);
      alert("Kunne ikke slette konsultasjon. Prøv igjen senere.");
    }
  };

  /**
   * Delete all consultations
   */
  const handleDeleteAllKonsultasjoner = async () => {
    if (!window.confirm("Er du sikker på at du vil slette alle konsultasjoner?")) {
      return;
    }
    
    try {
      await deleteAllKonsultasjonerService(apiBaseUrl, userId);
      
      setKonsultasjoner([]);
      setCurrentKonsultasjon(null);
      setTranscription('');
      setNote('');
    } catch (error) {
      console.error(`Error deleting all consultations for user ${userId}:`, error);
    }
  };

  /**
   * Generate a note for the current consultation
   * @param {string} chosenTemplate - The template to use
   * @param {Object} templates - Available templates
   * @param {string} defaultGptModel - The default GPT model
   */
  const generateNote = async (chosenTemplate, templates, defaultGptModel) => {
    if (!currentKonsultasjonRef.current) {
      alert("Ingen konsultasjon er valgt.");
      return;
    }
    
    if (!transcription) {
      alert("Ingen transkripsjon tilgjengelig.");
      return;
    }
    
    if (!userId) {
      alert("Ingen bruker-ID tilgjengelig.");
      return;
    }
    
    setIsGeneratingNote(true);
    
    try {
      const savedTranscript = transcription;
      const { note: newNote, title } = await generateNoteService(
        apiBaseUrl,
        userId,
        savedTranscript,
        currentKonsultasjonRef.current.chosen_gpt_model || defaultGptModel,
        templates[chosenTemplate] || "",
        contextText,
        currentKonsultasjonRef.current.id
      );

      const updated = { 
        ...currentKonsultasjonRef.current, 
        transcript: savedTranscript,
        notat: newNote, 
        title, 
        status: "fullført",
        kontekst: contextText,
        user_id: userId // Ensure user_id is correct
      };
      
      await updateKonsultasjon(updated);
      setNote(newNote);
      setTranscription(savedTranscript);
      
      return { note: newNote, title };
    } catch (error) {
      console.error("Error generating note:", error);
      
      if (error.response) {
        alert(`Feil ved generering av notat: ${error.response.data.error}`);
      } else if (error.request) {
        alert("Kunne ikke nå serveren. Sjekk at backend kjører.");
      } else {
        alert("Feil ved sending av forespørsel: " + error.message);
      }
      
      throw error;
    } finally {
      setIsGeneratingNote(false);
    }
  };

  return {
    konsultasjoner,
    currentKonsultasjon,
    setCurrentKonsultasjon,
    transcription,
    setTranscription,
    note,
    setNote,
    contextText,
    setContextText,
    isGeneratingNote,
    isTranscriptionLocked,
    setIsTranscriptionLocked,
    createNewKonsultasjon,
    updateKonsultasjon,
    handleDeleteKonsultasjon,
    handleDeleteAllKonsultasjoner,
    generateNote
  };
};

export default useConsultation;